/**
 * useSpaceMetadata.js
 * Place at: src/hooks/useSpaceMetadata.js
 *
 * Fetches and caches space metadata + current user role.
 * Cache TTL: 10 minutes (space info rarely changes)
 * Cache key: spaceId — one entry per space
 * Invalidated on: join, leave, mod actions
 */

import { useState, useEffect, useCallback, useRef } from "react";
import { fetchSpaceMetadata } from "../lib/spaceQueries";

const METADATA_CACHE_TTL = 10 * 60 * 1000; // 10 minutes
const metadataCache = new Map(); // module-level — shared across hook instances

export function useSpaceMetadata(spaceId, userId) {
  const [metadata,  setMetadata]  = useState(null);
  const [loading,   setLoading]   = useState(true);
  const [error,     setError]     = useState(null);
  const abortRef = useRef(null);

  const fetch = useCallback(async (force = false) => {
    if (!spaceId || !userId) return;

    // Check cache
    if (!force) {
      const cached = metadataCache.get(spaceId);
      if (cached && Date.now() - cached.fetchedAt < METADATA_CACHE_TTL) {
        setMetadata(cached.data);
        setLoading(false);
        return;
      }
    }

    if (abortRef.current) abortRef.current.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    setLoading(true);
    setError(null);

    try {
      const data = await fetchSpaceMetadata(spaceId, userId);
      if (controller.signal.aborted) return;

      metadataCache.set(spaceId, { data, fetchedAt: Date.now() });
      setMetadata(data);
    } catch (err) {
      if (err.name === "AbortError") return;
      console.error("useSpaceMetadata error:", err);
      setError("Failed to load space info.");
    } finally {
      if (!controller.signal.aborted) setLoading(false);
    }
  }, [spaceId, userId]);

  useEffect(() => {
    fetch();
    return () => { if (abortRef.current) abortRef.current.abort(); };
  }, [fetch]);

  // Expose invalidate so parent can force refresh after join/leave/mod actions
  const invalidate = useCallback(() => {
    metadataCache.delete(spaceId);
    fetch(true);
  }, [spaceId, fetch]);

  return { metadata, loading, error, invalidate };
}